/**
 * Holding-costs breakdown for the loan editor drawer (BRRRR short-term only) —
 * matches the design's `#finHoldingSection`. The monthly loan payment is read
 * from the live financing form; taxes/insurance + utilities are editable, and
 * the total net carry = (loan + taxes + utilities) × hold months.
 */
import { NumberInput } from "@/components/ui/number-input";

function fmtCurrency(value: number): string {
  return "$" + Math.round(Math.abs(value)).toLocaleString();
}

export interface HoldingCostsState {
  months: number;
  taxes: number;
  utilities: number;
}

interface FinancingHoldingCostsProps {
  state: HoldingCostsState;
  /** Live monthly loan payment from the short-term loan form. */
  monthlyEmi: number;
  onChange: (patch: Partial<HoldingCostsState>) => void;
}

export function FinancingHoldingCosts({
  state,
  monthlyEmi,
  onChange,
}: FinancingHoldingCostsProps) {
  const months = Math.max(1, state.months || 1);
  const monthlyHold = (monthlyEmi || 0) + state.taxes + state.utilities;
  const total = monthlyHold * months;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
          Holding Costs
        </span>
        <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          Hold
          <NumberInput
            min={1}
            max={36}
            step={1}
            value={state.months}
            parse={(raw) => Number(raw) || 1}
            onChange={(months) => onChange({ months })}
            className="h-7 w-14 px-2 text-right text-[11.5px] font-bold tabular-nums"
            aria-label="Hold months"
          />
          mo
        </label>
      </div>

      <div className="rounded-[10px] border border-border bg-muted/40 px-3.5">
        <Row label="Loan Payment" sub="from Financing">
          <span className="font-bold tabular-nums text-destructive">
            -{fmtCurrency(monthlyEmi)}
          </span>
        </Row>
        <Row label="Taxes & Insurance" bordered>
          <HoldInput
            value={state.taxes}
            onChange={(v) => onChange({ taxes: v })}
            ariaLabel="Monthly taxes and insurance"
          />
        </Row>
        <Row label="Utilities" bordered>
          <HoldInput
            value={state.utilities}
            onChange={(v) => onChange({ utilities: v })}
            ariaLabel="Monthly utilities"
          />
        </Row>
        <Row label="Total Net Holding" sub={`over ${months} mo`} bordered total>
          <span className="font-bold tabular-nums text-destructive">
            -{fmtCurrency(total)}
          </span>
        </Row>
      </div>
    </div>
  );
}

function Row({
  label,
  sub,
  bordered,
  total,
  children,
}: {
  label: string;
  sub?: string;
  bordered?: boolean;
  total?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={[
        "flex items-center justify-between gap-2.5 py-2.5 text-[13px]",
        bordered ? "border-t border-dashed border-border" : "",
        total ? "border-solid border-border font-bold" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="text-muted-foreground">
        {label}
        {sub && (
          <span className="ml-1.5 text-[11px] text-muted-foreground/70">{sub}</span>
        )}
      </span>
      {children}
    </div>
  );
}

function HoldInput({
  value,
  onChange,
  ariaLabel,
}: {
  value: number;
  onChange: (v: number) => void;
  ariaLabel: string;
}) {
  return (
    <div className="relative w-32">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] font-bold text-destructive">
        -$
      </span>
      <NumberInput
        min={0}
        step={10}
        value={value}
        onChange={onChange}
        className="h-8 pl-7 text-right text-[13px] font-bold tabular-nums text-destructive"
        aria-label={ariaLabel}
      />
    </div>
  );
}
